// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';

// Utilidad para decodificar JWT sin librerías pesadas
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// Crear el contexto
export const AuthContext = createContext();

// Proveedor del contexto
export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay información de autenticación almacenada
    const storedAuth = localStorage.getItem('auth');
    if (storedAuth) {
      try {
        const parsedAuth = JSON.parse(storedAuth);
        
        // Verificar expiración antes de restaurar la sesión inicial
        if (parsedAuth && parsedAuth.token) {
          const tokenData = parseJwt(parsedAuth.token);
          if (tokenData && tokenData.exp && Date.now() >= tokenData.exp * 1000) {
            console.warn('El token JWT almacenado ya expiró. Forzando logout inicial.');
            localStorage.removeItem('auth');
            setAuth(null);
            setIsLoading(false);
            return;
          }
        }
        
        setAuth(parsedAuth);
      } catch (error) {
        console.error('Error al parsear la información de autenticación:', error);
        localStorage.removeItem('auth');
      }
    }
    setIsLoading(false);
  }, []);

  const logout = () => {
    localStorage.removeItem('auth');
    setAuth(null);
  };

  // Efecto que monitorea si el token activo expiró en tiempo real
  useEffect(() => {
    if (!auth || !auth.token) return;

    const tokenData = parseJwt(auth.token);
    if (!tokenData || !tokenData.exp) return;

    const expirationTimeMs = tokenData.exp * 1000;
    
    // Chequear cada 10 segundos
    const intervalId = setInterval(() => {
      if (Date.now() >= expirationTimeMs) {
        logout();
        alert('Tu sesión ha vencido por seguridad. Por favor, vuelve a iniciar sesión.');
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [auth]);

  // Verificar si un usuario tiene un rol específico
  const hasRole = (requiredRole) => {
    if (!auth) return false;
    
    // Si requiredRole es un array, verifica si el usuario tiene alguno de esos roles
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(auth.role);
    }
    
    // Si requiredRole es un string, verifica si el usuario tiene ese rol específico
    return auth.role === requiredRole;
  };

  return (
    <AuthContext.Provider value={{ auth, setAuth, logout, hasRole, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
  
};

// Hook personalizado para facilitar el uso del contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};